import { DisplayMetrics, TDetailedStoryboard } from "pages/media/types";
import { CombinedVolatileSlice } from "store/types";
import { sliceResetFns } from "store/utils";
import { StateCreator } from "zustand";

type StoryboardState = {
  scriptsWordsCount?: number;
  readingTime?: number;
  metrics?: DisplayMetrics;
  storyboard?: TDetailedStoryboard;
  updates: number;
  highlightIdToScroll?: string;
  highlightRanks?: number[];
};

const initialState: StoryboardState = {
  scriptsWordsCount: undefined,
  readingTime: undefined,
  metrics: undefined,
  storyboard: undefined,
  updates: 0,
};

export interface StoryboardSlice {
  storyboardState: StoryboardState;
  setScriptsWordsCount: (scriptsWordsCount: number) => void;
  setReadingTime: (readingTime: number) => void;
  setMetrics: (metrics: DisplayMetrics) => void;
  setStoryboard: (
    updateOrNewState:
      | TDetailedStoryboard
      | ((oldState: TDetailedStoryboard | undefined) => TDetailedStoryboard)
  ) => void;
  resetStoryboardState: () => void;
  addUpdate: () => void;
  resetUpdates: () => void;
  setHighlightIdToScroll: (highlightId: string) => void;
  setHighlightRanks: (ranks: number[]) => void;
}

export const createStoryboardSlice: StateCreator<
  CombinedVolatileSlice,
  [["zustand/devtools", never], ["zustand/immer", never]],
  [],
  StoryboardSlice
> = (set) => {
  sliceResetFns.add(() => set({ storyboardState: initialState }));

  return {
    storyboardState: initialState,
    setStoryboardState: (value: StoryboardState) =>
      set({ storyboardState: value }, undefined, "setStoryboardState"),
    resetStoryboardState: () =>
      set({ storyboardState: initialState }, undefined, "resetStoryboardState"),
    setScriptsWordsCount: (scriptsWordsCount) =>
      set(
        (state) => {
          state.storyboardState.scriptsWordsCount = scriptsWordsCount;
        },
        undefined,
        "setScriptsWordsCount"
      ),
    setReadingTime: (readingTime) =>
      set(
        (state) => {
          state.storyboardState.readingTime = readingTime;
        },
        undefined,
        "setReadingTime"
      ),
    setMetrics: (metrics) =>
      set(
        (state) => {
          state.storyboardState.metrics = metrics;
        },
        undefined,
        "setMetrics"
      ),
    setStoryboard: (updateOrNewState) => {
      set(
        (state) => {
          if (typeof updateOrNewState === "function") {
            const updatedStoryboard = updateOrNewState(
              state.storyboardState.storyboard
            );
            state.storyboardState.storyboard = updatedStoryboard;
          } else {
            state.storyboardState.storyboard = updateOrNewState;
          }
        },
        undefined,
        "setStoryboard"
      );
    },
    addUpdate: () => {
      set(
        (state) => {
          state.storyboardState.updates += 1;
        },
        undefined,
        "addUpdate"
      );
    },
    resetUpdates: () => {
      set(
        (state) => {
          state.storyboardState.updates = 0;
        },
        undefined,
        "resetUpdates"
      );
    },
    setHighlightIdToScroll: (highlightId) => {
      set(
        (state) => {
          state.storyboardState.highlightIdToScroll = highlightId;
        },
        undefined,
        "setHighlightIdToScroll"
      );
    },
    setHighlightRanks: (ranks) => {
      set(
        (state) => {
          state.storyboardState.highlightRanks = ranks;
        },
        undefined,
        "setHighlightRanks"
      );
    },
  };
};
